import { useId, useState, type ChangeEvent, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Copy, formatAmountForDisplay, formatDisplayNumber, t } from '@stokr/components-library'
import { getAmountBucket } from 'shared/analytics'
import { ChartTabs, formatWholeMoney } from 'components/ui/chart'
import { SectionSkeleton } from 'components/sto/Section'
import { AmountBox, AmountInput, CurrencyPill, FieldLabel } from 'components/sto/KeyActions/KeyActions.styles'
import { GroupTitle, Label, Row } from 'components/sto/KeyInformation/KeyInformation.styles'
import { stoQueries } from 'hooks/sto/queries/stoQueries'
import { sanitizeAmount } from 'hooks/sto/invest-panel.utils'
import { useStoTrack } from 'hooks/sto/useStoTracking'
import { loanTermsFor } from 'constants/loanTerms'
import { useStoProject } from 'hooks/sto/useStoProject'
import { useIsBelowDesktop } from 'hooks/sto/useIsBelowDesktop'
import { calculateLoan, convertLoanAmount, loanMaturity, type LoanInputMode } from 'models/loanCalculator'
import { formatUtcDate } from 'utils/formatUtcDate'
import {
  Caption,
  Card,
  Control,
  Derived,
  DerivedValue,
  Disclaimer,
  Footer,
  FooterText,
  Inputs,
  OfferButton,
  Pair,
  PriceLine,
  ResultColumn,
  ResultNote,
  Results,
  ResultValue,
  TabsSlot,
} from './LoanCalculator.styles'

const BTC_DECIMALS = 8
const DEFAULT_LOAN = '200000'

const usd = (value: number) => formatWholeMoney(value, 'USD')
const btcAmount = (value: number) =>
  `${formatDisplayNumber(value, { minimumFractionDigits: BTC_DECIMALS, maximumFractionDigits: BTC_DECIMALS })} BTC`
const percent = (value: number) => `${Math.round(value * 100)}%`
/** `10.5%`: up to two decimals, free of float noise (0.115 × 100 = 11.500000000000002). */
const ratePercent = (value: number) => `${formatDisplayNumber(value * 100, { maximumFractionDigits: 2 })}%`
/** Grouped while typing, keeping a trailing "." so a decimal can be typed (as Invest's amount). */
const toDisplay = (raw: string) =>
  raw.endsWith('.') ? `${formatAmountForDisplay(raw.slice(0, -1))}.` : formatAmountForDisplay(raw)

const ResultRow = ({
  label,
  value,
  note,
  tone,
}: {
  label: ReactNode
  value: ReactNode
  note?: ReactNode
  tone?: 'warning' | 'danger'
}) => (
  <Row>
    <Label>{label}</Label>
    <ResultValue $tone={tone}>
      {value}
      {note && <ResultNote>{note}</ResultNote>}
    </ResultValue>
  </Row>
)

/**
 * The funds' Bitcoin-backed loan calculator, on the terms in `constants/loanTerms` (the default,
 * or the fund's own). Start from the USD to borrow or the BTC to pledge; LTV and term pick the rest.
 * Everything is a calculation on the live BTC price, which loads in the browser: until then the
 * inputs show and the figures wait.
 */
export const LoanCalculator = () => {
  const trackSto = useStoTrack()
  const { project } = useStoProject()
  const terms = loanTermsFor(project?.name)
  const hintId = useId()
  // Four terms fit a phone only compact and short ("12 mo").
  const isMobile = useIsBelowDesktop()
  const { data: rates } = useQuery(stoQueries.exchangeRates(true))
  const btcPrice = rates?.btcInUsd && Number.isFinite(rates.btcInUsd) ? rates.btcInUsd : undefined
  const [mode, setMode] = useState<LoanInputMode>('usd')
  const [amount, setAmount] = useState(DEFAULT_LOAN)
  const [ltv, setLtv] = useState<number>(terms.defaultLtv)
  const [months, setMonths] = useState<number>(terms.defaultTermMonths)
  // Read once in the browser, where the figures render: the repayment date counts from it.
  const [now] = useState(Date.now)

  const quote = btcPrice
    ? calculateLoan({ mode, amount: Number(amount) || 0, ltv, months, btcPrice }, terms)
    : undefined
  // Nothing typed yet is not "below the minimum".
  const outOfRange = Boolean(Number(amount)) && (quote?.range === 'below' || quote?.range === 'above')
  const trackProps = () => ({
    mode,
    ltv: percent(ltv),
    months,
    amount_bucket: quote ? getAmountBucket(quote.loan) : undefined,
    range: quote?.range,
  })

  // The request carries the figures picked here, so the lender can answer without asking again.
  const offerHref = `mailto:${terms.offerEmail}?${new URLSearchParams({
    subject: t('stoPage.borrow.calculator.offerEmail.subject', {
      project: project?.title?.trim() || project?.name || '',
    }),
    body: t('stoPage.borrow.calculator.offerEmail.body', {
      loan: quote ? usd(quote.loan) : '-',
      btc: quote ? btcAmount(quote.btc) : '-',
      ltv: percent(ltv),
      months,
    }),
  })
    .toString()
    .replace(/\+/g, '%20')}`

  const switchMode = (next: LoanInputMode) => {
    if (next === mode || !btcPrice) return
    trackSto('sto_loan_mode_selected', { mode: next })
    const converted = convertLoanAmount(Number(amount) || 0, next, ltv, btcPrice)
    setAmount(next === 'btc' ? converted.toFixed(BTC_DECIMALS) : String(Math.round(converted)))
    setMode(next)
  }

  const rangeHint =
    outOfRange && quote
      ? t(
          quote.range === 'below' ? 'stoPage.borrow.calculator.belowMinimum' : 'stoPage.borrow.calculator.aboveMaximum',
          {
            loan: usd(quote.loan),
          },
        )
      : t('stoPage.borrow.calculator.range', { min: usd(terms.minLoan), max: usd(terms.maxLoan) })

  return (
    <>
      <Card data-cy="sto-loan-calculator">
        <Inputs>
          <Control>
            <FieldLabel>
              <Copy id="stoPage.borrow.calculator.startFrom" />
            </FieldLabel>
            <TabsSlot>
              <ChartTabs<LoanInputMode>
                wide
                options={[
                  { value: 'usd', label: t('stoPage.borrow.calculator.modeUsd') },
                  { value: 'btc', label: t('stoPage.borrow.calculator.modeBtc') },
                ]}
                value={mode}
                onChange={switchMode}
                label={t('stoPage.borrow.calculator.startFrom')}
                dataCy="sto-loan-mode"
              />
            </TabsSlot>
          </Control>

          <Pair>
            <Control>
              <FieldLabel>
                <Copy
                  id={
                    mode === 'usd'
                      ? 'stoPage.borrow.calculator.amountToBorrow'
                      : 'stoPage.borrow.calculator.btcToPledge'
                  }
                />
              </FieldLabel>
              <AmountBox $tone="editable">
                <AmountInput
                  data-keep-font-size
                  type="text"
                  inputMode={mode === 'usd' ? 'numeric' : 'decimal'}
                  autoComplete="off"
                  placeholder="0"
                  aria-label={t(
                    mode === 'usd'
                      ? 'stoPage.borrow.calculator.amountToBorrow'
                      : 'stoPage.borrow.calculator.btcToPledge',
                  )}
                  value={toDisplay(amount)}
                  aria-describedby={hintId}
                  data-cy="sto-loan-amount-input"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setAmount(sanitizeAmount(event.target.value.replace(/,/g, ''), mode === 'usd' ? 0 : BTC_DECIMALS))
                  }
                  onBlur={() => quote && trackSto('sto_loan_amount_entered', trackProps())}
                />
                <CurrencyPill $muted={false}>{mode === 'usd' ? 'USD' : 'BTC'}</CurrencyPill>
              </AmountBox>
              <Caption id={hintId} $warn={outOfRange}>
                {rangeHint}
              </Caption>
            </Control>

            <Control>
              <FieldLabel>
                <Copy
                  id={
                    mode === 'usd' ? 'stoPage.borrow.calculator.btcToPledge' : 'stoPage.borrow.calculator.maxToBorrow'
                  }
                />
              </FieldLabel>
              {quote ? (
                <Derived data-cy="sto-loan-derived">
                  <DerivedValue>{mode === 'usd' ? btcAmount(quote.btc) : usd(quote.loan)}</DerivedValue>
                  <Caption>
                    {mode === 'usd' ? (
                      <Copy id="stoPage.borrow.calculator.collateralValue" values={{ value: usd(quote.collateral) }} />
                    ) : (
                      <Copy
                        id="stoPage.borrow.calculator.ofCollateral"
                        values={{ ltv: percent(ltv), value: usd(quote.collateral) }}
                      />
                    )}
                  </Caption>
                </Derived>
              ) : (
                <SectionSkeleton rows={1} rowHeight={74} />
              )}
            </Control>
          </Pair>

          <Control>
            <FieldLabel>
              <Copy id="stoPage.borrow.calculator.ltv" />
            </FieldLabel>
            <TabsSlot>
              <ChartTabs<string>
                wide
                options={terms.ltvOptions.map((option) => ({ value: String(option), label: percent(option) }))}
                value={String(ltv)}
                onChange={(value) => {
                  trackSto('sto_loan_ltv_selected', { ltv: percent(Number(value)) })
                  setLtv(Number(value))
                }}
                label={t('stoPage.borrow.calculator.ltv')}
                dataCy="sto-loan-ltv"
              />
            </TabsSlot>
          </Control>

          <Control>
            <FieldLabel>
              <Copy id="stoPage.borrow.calculator.term" />
            </FieldLabel>
            <TabsSlot>
              <ChartTabs<string>
                wide={!isMobile}
                options={terms.termMonths.map((option) => ({
                  value: String(option),
                  label: t(isMobile ? 'stoPage.borrow.calculator.monthsShort' : 'stoPage.borrow.calculator.months', {
                    count: option,
                  }),
                }))}
                value={String(months)}
                onChange={(value) => {
                  trackSto('sto_loan_term_selected', { months: Number(value) })
                  setMonths(Number(value))
                }}
                label={t('stoPage.borrow.calculator.term')}
                dataCy="sto-loan-term"
              />
            </TabsSlot>
          </Control>

          <PriceLine>
            <FieldLabel as="span">
              <Copy id="stoPage.borrow.calculator.btcPrice" />
            </FieldLabel>
            {btcPrice ? (
              <>
                <strong>{usd(btcPrice)}</strong>
                <Caption as="span">
                  <Copy id="stoPage.borrow.calculator.asOf" values={{ date: formatUtcDate(now) ?? '' }} />
                </Caption>
              </>
            ) : (
              <Caption as="span">–</Caption>
            )}
          </PriceLine>
        </Inputs>

        {quote && (
          <Results data-cy="sto-loan-results">
            <ResultColumn>
              <GroupTitle>
                <Copy id="stoPage.borrow.calculator.facility" />
              </GroupTitle>
              <ResultRow label={<Copy id="stoPage.borrow.calculator.loanAmount" />} value={usd(quote.loan)} />
              <ResultRow label={<Copy id="stoPage.borrow.calculator.ltv" />} value={percent(ltv)} />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.term" />}
                value={t('stoPage.borrow.calculator.months', { count: months })}
              />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.rate" />}
                value={t('stoPage.borrow.calculator.ratePa', { rate: ratePercent(terms.rate) })}
              />
              <ResultRow label={<Copy id="stoPage.borrow.calculator.interest" />} value={usd(quote.interest)} />
              <ResultRow label={<Copy id="stoPage.borrow.calculator.total" />} value={usd(quote.total)} />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.repayment" />}
                value={<Copy id="stoPage.borrow.calculator.singlePayment" />}
                note={
                  <Copy
                    id="stoPage.borrow.calculator.dueOn"
                    values={{ date: formatUtcDate(loanMaturity(now, months)) ?? '' }}
                  />
                }
              />
            </ResultColumn>

            <ResultColumn>
              <GroupTitle>
                <Copy id="stoPage.borrow.calculator.collateral" />
              </GroupTitle>
              <ResultRow label={<Copy id="stoPage.borrow.calculator.asset" />} value="BTC" />
              <ResultRow label={<Copy id="stoPage.borrow.calculator.pledged" />} value={btcAmount(quote.btc)} />
              <ResultRow label={<Copy id="stoPage.borrow.calculator.valueAtPledge" />} value={usd(quote.collateral)} />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.custody" />}
                value={terms.custody}
                note={terms.custodian}
              />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.rehypothecation" />}
                value={<Copy id="stoPage.borrow.calculator.none" />}
              />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.released" />}
                value={<Copy id="stoPage.borrow.calculator.releasedValue" />}
              />
            </ResultColumn>

            <ResultColumn>
              <GroupTitle>
                <Copy id="stoPage.borrow.calculator.riskLevels" />
              </GroupTitle>
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.firstMarginCall" />}
                value={usd(quote.firstMarginCallPrice)}
                note={t('stoPage.borrow.calculator.atLtv', { ltv: percent(terms.firstMarginCall) })}
                tone="warning"
              />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.secondMarginCall" />}
                value={usd(quote.secondMarginCallPrice)}
                note={t('stoPage.borrow.calculator.atLtv', { ltv: percent(terms.secondMarginCall) })}
                tone="warning"
              />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.liquidation" />}
                value={usd(quote.liquidationPrice)}
                note={t('stoPage.borrow.calculator.atLtv', { ltv: percent(terms.liquidation) })}
                tone="danger"
              />
              <ResultRow label={<Copy id="stoPage.borrow.calculator.loanCurrency" />} value={terms.loanCurrencies} />
              <ResultRow
                label={<Copy id="stoPage.borrow.calculator.creditCheck" />}
                value={<Copy id="stoPage.borrow.calculator.none" />}
              />
            </ResultColumn>
          </Results>
        )}

        <Footer>
          <FooterText>
            <Copy id="stoPage.borrow.calculator.termsNote" />
          </FooterText>
          <OfferButton
            as="a"
            href={offerHref}
            data-cy="sto-loan-request-offer"
            onClick={() => trackSto('sto_loan_offer_clicked', trackProps())}>
            <Copy id="stoPage.borrow.calculator.requestOffer" />
          </OfferButton>
        </Footer>
      </Card>
      <Disclaimer>
        <Copy id="stoPage.borrow.calculator.disclaimer" />
      </Disclaimer>
    </>
  )
}
